import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLabel, IPLink, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import GenericModal from 'components/Dialogs/GenericModal';
import HrefLink from 'components/Links/HrefLink';
import { LearnMore } from 'components/Notification/Info';
import InfoMessage from 'components/Notification/InfoMessage';
import SingleTask from 'components/Tasks/SingleTask';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalKB_howToWorkWithKPIEvidence } from 'globalConstants';
import { globalStackTokensGapMedium } from 'globalStyles';
import KPIDataTransform from 'models/kpi/kpiDataTransform';
import Task, { TaskTypes } from 'models/tasks/task';
import { TaskKinds } from 'models/tasks/taskType';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { apiTestKPINotAcceptedEvidenceEvent } from 'services/Api/kpiService';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';

interface IKPIEvidenceTransformModalProps {
  transform: KPIDataTransform;
  isOpen: boolean;
  onClose: () => void;
  onSave: (transform: KPIDataTransform) => void;
}

const NotAcceptedEvidenceConfigModal = (props: IKPIEvidenceTransformModalProps) => {
  const appContext = React.useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpi', 'task']);
  const [transform, setTransform] = React.useState<KPIDataTransform>(props.transform);
  const [lastEvent, setLastEvent] = React.useState<Task | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isTesting, setIsTesting] = React.useState<boolean>(false);
  const [showLastEvent, setShowLastEvent] = React.useState<boolean>(false);

  //
  // Effects
  //
  React.useEffect(() => {
    //react to another KPI
    if (props.isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const loadData = async () => {
    try {
      if (!props.transform || !props.transform.lastEventId) return;

      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const taskCol = await apiGetSingleTask(
        props.transform.lastEventId,
        false,
        accessToken,
        appContext.globalDataCache,
      );

      if (taskCol && taskCol.tasks.length === 1) {
        setLastEvent(taskCol.tasks[0]);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = () => {
    props.onSave(transform);
  };

  const getTemplateOptions = (): IPSelectableOption[] => {
    const eventTemplates = appContext.globalDataCache.templates.items.filter(
      (t) => t.taskType === TaskTypes.EventTemplate && t.taskKind === TaskKinds.None,
    );

    let options: IPSelectableOption[] = [];

    if (eventTemplates.length === 0) {
      options.push({
        key: -1,
        text: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.TemplateNoItems'),
      });
    } else {
      options = [
        {
          key: 0,
          text: '',
        },
      ];

      options.push(
        ...eventTemplates.map((t) => {
          return {
            key: t.taskId,
            text: t.name,
          };
        }),
      );
    }

    return options;
  };

  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    try {
      if (!isCancelled) {
        setLastEvent(task);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  const removeTask = async (task: Task) => {
    setLastEvent(undefined);
  };

  const updateTask = async (task: Task) => {
    try {
      setLastEvent(task);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const testEvent = async () => {
    try {
      setIsLoading(true);
      setIsTesting(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const task = await apiTestKPINotAcceptedEvidenceEvent(transform, accessToken, appContext.globalDataCache);

      if (task) {
        setLastEvent(task);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
      setIsTesting(false);
    }
  };

  //
  // Main render
  //
  return (
    <GenericModal
      title={t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onSave={onSave}
      minWidth={250}
      width={'50vw'}
      maxWidth={'95vw'}
      minHeight={300}
      height={'75vh'}
      maxHeight={'90vh'}
      hideFooter={false}
    >
      <IPSeparator />
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <ScrollableStackItem>
          <IPStack tokens={globalStackTokensGapMedium}>
            <IPStack.Item>
              <IPText block>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.SubTitle')}</IPText>
              <LearnMore learnMoreLink={globalKB_howToWorkWithKPIEvidence} />
            </IPStack.Item>
            <IPStack.Item>
              <IPLabel>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Template')}</IPLabel>
              <IPDropdown
                selectedKey={transform.templateId ?? 0}
                options={getTemplateOptions()}
                styles={{ listbox: { maxHeight: 250 } }}
                onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                  const templateId = option?.key as number;
                  if (templateId >= 0) {
                    const newTransform = transform.clone();
                    newTransform.templateId = templateId;
                    setTransform(newTransform);
                  }
                }}
              />
              <HrefLink href="/admin/content/templates" styles={{ root: { padding: 5 } }}>
                {t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.TemplateLink')}
              </HrefLink>
            </IPStack.Item>
            <IPStack.Item>
              <IPText block>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.TestInfo')}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack horizontal tokens={globalStackTokensGapMedium} verticalAlign="center">
                <IPStack.Item>
                  <IPDefaultButton
                    disabled={isLoading || !transform.templateId || (lastEvent && !lastEvent.isCompleted())}
                    text={t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.TestButton')}
                    onClick={() => testEvent()}
                  />
                </IPStack.Item>
                {!isTesting && (
                  <IPStack.Item>
                    <IPLink onClick={() => setShowLastEvent(true)} disabled={!lastEvent || isLoading}>
                      {t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.TestLink')}
                    </IPLink>
                  </IPStack.Item>
                )}
                {isTesting && (
                  <IPStack.Item>
                    <IPSpinner />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <InfoMessage message={t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Info1')}>
                <ul>
                  <li>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Info2')}</li>
                  <li>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Info3')}</li>
                  <li>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Info4')}</li>
                  <li>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Info5')}</li>
                  <li>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Info6')}</li>
                  <li>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceConfig.Info7')}</li>
                </ul>
              </InfoMessage>
            </IPStack.Item>
          </IPStack>
        </ScrollableStackItem>
        {lastEvent && (
          <SingleTask
            task={lastEvent}
            isOpen={showLastEvent}
            close={() => {
              setShowLastEvent(false);
            }}
            onSave={saveTask}
            onRemove={removeTask}
            onUpdate={updateTask}
          />
        )}
      </IPStack>
    </GenericModal>
  );
};

export default NotAcceptedEvidenceConfigModal;
